import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Shield, Lock, AlertCircle, Eye, EyeOff, Check, X, Loader2, KeyRound } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminPinGate Component
 * 
 * Wraps sensitive admin content and requires PIN verification.
 * PIN is hashed server-side and never stored in plaintext.
 * 
 * Usage:
 * <AdminPinGate onVerified={() => setCanAccess(true)}>
 *   <SensitiveContent />
 * </AdminPinGate>
 */
const AdminPinGate = ({ children, onVerified, title = "Payment Settings" }) => {
  const { isDark } = useTheme();
  const [status, setStatus] = useState(null); // null = loading, object = loaded
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [setting, setSetting] = useState(false);
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '', '', '']);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState(null);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const inputRefs = useRef([]);
  const confirmInputRefs = useRef([]);

  useEffect(() => {
    checkPinStatus();
  }, []);

  const checkPinStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin-pin/status`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        
        // If already verified, notify parent
        if (data.is_verified && onVerified) {
          onVerified();
        }
        
        // If PIN not set, show setup screen
        if (!data.pin_is_set) {
          setIsSettingUp(true);
        }
      } else {
        setError('Failed to check PIN status');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (index, value, isConfirm = false) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const pinArray = isConfirm ? [...confirmPin] : [...pin];
    pinArray[index] = value;
    
    if (isConfirm) {
      setConfirmPin(pinArray);
    } else {
      setPin(pinArray);
    }
    
    // Auto-focus next input
    if (value && index < 5) {
      const refs = isConfirm ? confirmInputRefs : inputRefs;
      refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e, isConfirm = false) => {
    const refs = isConfirm ? confirmInputRefs : inputRefs;
    const pinArray = isConfirm ? confirmPin : pin;
    
    if (e.key === 'Backspace' && !pinArray[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
    
    // Allow paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      return;
    }
  };

  const handlePaste = (e, isConfirm = false) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newPin = [...(isConfirm ? confirmPin : pin)];
    
    for (let i = 0; i < pastedData.length; i++) {
      newPin[i] = pastedData[i];
    }
    
    if (isConfirm) {
      setConfirmPin(newPin);
    } else {
      setPin(newPin);
    }
  };

  const verifyPin = async () => {
    const pinString = pin.join('');
    if (pinString.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }

    setVerifying(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin-pin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin: pinString })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ ...status, is_verified: true });
        if (onVerified) onVerified();
      } else {
        setError(data.detail || 'Verification failed');
        setPin(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        
        // Refresh status to get updated lockout info
        checkPinStatus();
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setVerifying(false);
    }
  };

  const setupPin = async () => {
    const pinString = pin.join('');
    const confirmString = confirmPin.join('');
    
    if (pinString.length < 4) {
      setError('PIN must be at least 4 digits');
      return;
    }
    
    if (pinString !== confirmString) {
      setError('PINs do not match');
      return;
    }

    setSetting(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin-pin/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin: pinString })
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ ...status, pin_is_set: true, is_verified: true });
        setIsSettingUp(false);
        if (onVerified) onVerified();
      } else {
        setError(data.detail || 'Setup failed');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setSetting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#A2FF00] animate-spin" />
      </div>
    );
  }

  // Already verified - show children
  if (status?.is_verified) {
    return <>{children}</>;
  }

  // Locked out
  if (status?.locked_out) {
    const minutes = Math.ceil(status.lockout_remaining_seconds / 60);
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className={`max-w-md w-full mx-4 p-8 rounded-2xl ${isDark ? 'bg-[#111] border-red-500/30' : 'bg-white border-red-200'} border text-center`}>
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <Lock className="w-8 h-8 text-red-400" />
          </div>
          <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Account Locked
          </h2>
          <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Too many failed PIN attempts. Please wait {minutes} minute{minutes > 1 ? 's' : ''} before trying again.
          </p>
          <div className={`text-3xl font-mono font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
            {Math.floor(status.lockout_remaining_seconds / 60)}:{(status.lockout_remaining_seconds % 60).toString().padStart(2, '0')}
          </div>
        </div>
      </div>
    );
  }

  // Setup PIN screen
  if (isSettingUp || !status?.pin_is_set) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <div className={`max-w-md w-full mx-4 p-8 rounded-2xl ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border`}>
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#A2FF00]/20 flex items-center justify-center">
              <KeyRound className="w-8 h-8 text-[#A2FF00]" />
            </div>
            <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Set Up Security PIN
            </h2>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Create a 4-6 digit PIN to protect access to {title}
            </p>
          </div>

          {/* Security Note */}
          <div className={`mb-6 p-3 rounded-lg ${isDark ? 'bg-green-500/10 border-green-500/30' : 'bg-green-50 border-green-200'} border`}>
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
              <p className={`text-xs ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                Your PIN is securely hashed and encrypted. It is never stored in plaintext.
              </p>
            </div>
          </div>

          {/* Enter PIN */}
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Enter PIN
            </label>
            <div className="flex justify-center gap-2">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type={showPin ? 'text' : 'password'}
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className={`w-10 h-12 text-center text-xl font-bold rounded-lg border ${
                    isDark 
                      ? 'bg-[#0A0A0A] border-white/20 text-white focus:border-[#A2FF00]' 
                      : 'bg-gray-100 border-gray-300 text-gray-900 focus:border-green-500'
                  } focus:outline-none focus:ring-2 focus:ring-[#A2FF00]/30`}
                />
              ))}
            </div>
          </div>

          {/* Confirm PIN */}
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Confirm PIN
            </label>
            <div className="flex justify-center gap-2">
              {confirmPin.map((digit, index) => (
                <input
                  key={index}
                  ref={el => confirmInputRefs.current[index] = el}
                  type={showPin ? 'text' : 'password'}
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value, true)}
                  onKeyDown={(e) => handleKeyDown(index, e, true)}
                  onPaste={(e) => handlePaste(e, true)}
                  className={`w-10 h-12 text-center text-xl font-bold rounded-lg border ${
                    isDark 
                      ? 'bg-[#0A0A0A] border-white/20 text-white focus:border-[#A2FF00]' 
                      : 'bg-gray-100 border-gray-300 text-gray-900 focus:border-green-500'
                  } focus:outline-none focus:ring-2 focus:ring-[#A2FF00]/30`}
                />
              ))}
            </div>
          </div>

          {/* Show PIN toggle */}
          <div className="flex items-center justify-center gap-2 mb-4">
            <button
              onClick={() => setShowPin(!showPin)}
              className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {showPin ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {showPin ? 'Hide' : 'Show'} PIN
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'}`}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={setupPin}
            disabled={setting || pin.filter(d => d).length < 4 || confirmPin.filter(d => d).length < 4}
            className="w-full py-3 bg-[#A2FF00] text-black font-bold rounded-lg hover:bg-[#8EFF00] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {setting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Setting up...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Set PIN
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Verify PIN screen
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className={`max-w-md w-full mx-4 p-8 rounded-2xl ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border`}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#A2FF00]/20 flex items-center justify-center">
            <Shield className="w-8 h-8 text-[#A2FF00]" />
          </div>
          <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Enter Security PIN
          </h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Enter your PIN to access {title}
          </p>
        </div>

        {/* PIN Input */}
        <div className="mb-4">
          <div className="flex justify-center gap-2">
            {pin.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type={showPin ? 'text' : 'password'}
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onKeyUp={(e) => {
                  if (e.key === 'Enter' && pin.filter(d => d).length >= 4) {
                    verifyPin();
                  }
                }}
                className={`w-10 h-12 text-center text-xl font-bold rounded-lg border ${
                  isDark 
                    ? 'bg-[#0A0A0A] border-white/20 text-white focus:border-[#A2FF00]' 
                    : 'bg-gray-100 border-gray-300 text-gray-900 focus:border-green-500'
                } focus:outline-none focus:ring-2 focus:ring-[#A2FF00]/30`}
              />
            ))}
          </div>
        </div>

        {/* Show PIN toggle */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <button
            onClick={() => setShowPin(!showPin)}
            className={`flex items-center gap-1 text-xs ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {showPin ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showPin ? 'Hide' : 'Show'} PIN
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${isDark ? 'bg-red-500/10 text-red-400' : 'bg-red-50 text-red-600'}`}>
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Failed attempts warning */}
        {status?.failed_attempts > 0 && (
          <div className={`mb-4 p-3 rounded-lg ${isDark ? 'bg-yellow-500/10 text-yellow-400' : 'bg-yellow-50 text-yellow-700'}`}>
            <p className="text-sm">
              {status.max_attempts - status.failed_attempts} attempts remaining
            </p>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={verifyPin}
          disabled={verifying || pin.filter(d => d).length < 4}
          className="w-full py-3 bg-[#A2FF00] text-black font-bold rounded-lg hover:bg-[#8EFF00] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {verifying ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Verifying...
            </>
          ) : (
            <>
              <Lock className="w-4 h-4" />
              Unlock
            </>
          )}
        </button>

        {/* Session info */}
        <p className={`mt-4 text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          PIN verification lasts for {status?.verification_duration_minutes || 30} minutes
        </p>
      </div>
    </div>
  );
};

export default AdminPinGate;
